import React from "react";
import HeaderComponent from "components/card/HeaderComponent";
import { DatePicker, Form, Checkbox, Divider } from "antd";
import dayjs from "dayjs";
import { requiredField } from "utils/crudSagaHelper";
import FormItem from "components/fields/FormItem";
import { CircleAlert, Play } from "lucide-react";

export default function ConfigureReport({
  selectedReport,
  onSelectReport,
  analyticsCards,
  onGenerateReport,
  selectedDepartments,
  setSelectedDepartments,
  dateRange,
  setDateRange,
  setActiveTab,
}) {
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();

  const isReportSelected = Boolean(selectedReport);
  const hasDepartmentsSelected = selectedDepartments.length > 0;

  const teamOptions = [
    "Engineering",
    "Marketing",
    "Customer Success",
    "Design",
    "Operations",
  ];

  return (
    <div className="flex flex-col">
      <HeaderComponent
        title="Configuration"
        description="Customize your report"
        descriptionClass="text-sm text-gray-700"
        bgcolor="bg-gradient-to-br from-red-50 to-orange-50"
      >
        <Form form={form} layout="vertical">
          <FormItem label="Date Range" className="text-[8px]" rules={[requiredField]}>
            <RangePicker
              className="w-full rounded-lg border-2"
              format="MMM DD, YYYY"
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
          </FormItem>

          <FormItem label="Departments" className="text-[8px]" rules={[requiredField]}>
            <div className="rounded-lg border-2 border-slate-200 p-3">
              <Checkbox.Group
                options={teamOptions}
                value={selectedDepartments}
                onChange={(checked) => setSelectedDepartments(checked)}
                className="emerald-checkbox flex flex-col text-xs"
              />
            </div>
          </FormItem>

          <Divider style={{ margin: "0.5rem 0", borderColor: "#e5e7eb" }} />

          <div className="space-y-2 mt-4">
            <button
              type="button"
              className={`flex justify-center rounded-xl items-center h-9 font-semibold w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white gap-2
                ${!selectedReport || selectedDepartments.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={!selectedReport || selectedDepartments.length === 0}
              onClick={() => {
                const reportId = "RPT" + Date.now();

                const newReport = {
                  title: selectedReport,
                  reportId,
                  dateRange: `${dateRange[0].format("MMM DD, YYYY")} - ${dateRange[1].format("MMM DD, YYYY")}`,
                  departments: selectedDepartments,
                  generatedAt: dayjs().format("HH:mm"),
                  icon: analyticsCards.find(c => c.title === selectedReport)?.icon,
                  justGenerated: true,
                };

                onGenerateReport(newReport);
              }}
            >
              <Play className="w-4 h-4" /> Generate Report
            </button>

            <button
              type="button"
              onClick={() => {
                onSelectReport(null);
                setSelectedDepartments([]);
                setDateRange([dayjs("2025-10-01"), dayjs("2025-10-13")]);
              }}
              className="flex justify-center rounded-xl items-center h-9 font-semibold w-full border-2 gap-2 hover:bg-gray-50"
            >
              Reset Configuration
            </button>

            {isReportSelected && hasDepartmentsSelected && (
              <div className="p-3 rounded-lg bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200">
                <div className="flex items-start gap-2">
                  <CircleAlert className="w-4 h-4 text-orange-600" />
                  <div className="text-[11px] text-orange-900">
                    <p className="font-semibold mb-1">Ready to Generate</p>
                    <p>
                      Your report will include data from{" "}
                      {selectedDepartments.length === 0 || selectedDepartments.length === teamOptions.length
                        ? "all"
                        : selectedDepartments.length}{" "}
                      {selectedDepartments.length === 1 ? "department" : "departments"} for the selected date range.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Form>
      </HeaderComponent>
    </div>
  );
}
